// @ts-strict-ignore
import { unEscape } from '@/shared/lib/utils/string/un-escape'
import { withoutQuot } from '@/shared/lib/utils/string/withoutQuot'
import { TFaqItem, TPage } from '@/shared/types/deprecated-types'
import { TProduct } from '@/shared/types/product-types'

export type TProjectInfo = {
	link: string
	uploadsLink: string
	name: string
	description: string
	logoSrc: string
	socialLinks: string[]
	addressLocality: string
	postalCode: string
	streetAddress: string
	faxNumber?: string
}

/**
 * Microdata jsonLD generator
 */
export class JsonLD {
	private readonly _projectInfo: TProjectInfo
	private readonly _commonSchemaParams: Record<string, unknown> = {
		'@context': `https://schema.org`
	}

	/**
	 * Passing general information about the project
	 * @param projectInfo - General information about the current project, see {@link iPortInfo}
	 */
	constructor(projectInfo: TProjectInfo) {
		this._projectInfo = projectInfo
	}

	/**
	 * Returns Organization info jsonLd scheme (Organization)
	 *
	 * @see https://schema.org/Organization
	 */
	public getOrganizationSchema(): string {
		return this._prepareData({
			'@graph': [
				{
					'@type': `Organization`,
					url: this._projectInfo.link,
					name: this._projectInfo.name,
					description: withoutQuot(this._projectInfo.description),
					logo: this._projectInfo.logoSrc,
					sameAs: this._projectInfo.socialLinks,
					address: {
						'@type': `PostalAddress`,
						addressLocality: this._projectInfo.addressLocality,
						postalCode: this._projectInfo.postalCode,
						streetAddress: this._projectInfo.streetAddress
					},
					faxNumber: this._projectInfo.faxNumber
				}
			]
		})
	}

	/**
	 * Returns product item jsonLD scheme (Product)
	 * @param product
	 * @param categoryName
	 *
	 * @see https://schema.org/Product
	 */
	public getProductSchema(product: TProduct, categoryName: string): string {
		const brandProp = product.PROPERTIES?.osnovnye_parametry?.GROUP_PROPERTIES?.PROIZVODITEL
		const brandValue = brandProp ? brandProp.values[0] : ``

		return this._prepareData({
			'@type': `Product`,
			url: `${this._projectInfo.link}/products/productID${product.KOD}/`,
			name: withoutQuot(product.TITLE),
			description: withoutQuot(product.SEO?.ELEMENT_META_DESCRIPTION),
			image: product.IMAGES && product.IMAGES.map(path => this._projectInfo.uploadsLink + path),
			mpn: product.ARTICLE,
			category: categoryName,
			brand: {
				'@type': `Brand`,
				name: brandValue
			},
			offers: {
				'@type': `Offer`,
				url: `${this._projectInfo.link}/products/productID${product.KOD}/`,
				availability: `https://schema.org/InStock`,
				price: product.PRICE.VALUE,
				priceCurrency: `RUB`
			}
		})
	}

	/**
	 * Returns catalog-old listing jsonLD scheme (ItemList)
	 *
	 * @see https://schema.org/ItemList
	 */
	public getListingSchema(categoryCode: string, products: TProduct[] = []): string {
		return this._prepareData({
			'@type': `ItemList`,
			url: `${this._projectInfo.link}/catalog/${categoryCode}/`,
			numberOfItems: products.length,
			itemListOrder: `Unordered`
			// itemListElement: products.map((product, i) => ({
			// 	'@type': `ListItem`,
			// 	position: i + 1,
			// 	item: {
			// 		'@type': `Product`,
			// 		image: `${this._projectInfo.uploadsLink}${
			// 			product?.IMAGE || product.IMAGES?.[0] || `/product-image-placeholder.jpeg`
			// 		}`,
			// 		url: `${this._projectInfo.link}/products/productID${product.KOD}/`,
			// 		name: product.TITLE,
			// 		mpn: product.ARTICLE,
			// 		offers: {
			// 			'@type': `Offer`,
			// 			availability: `https://schema.org/InStock`,
			// 			priceCurrency: `RUB`,
			// 			price: product.PRICE.VALUE
			// 		}
			// 	}
			// }))
		})
	}

	/**
	 * Returns FAQ jsonLD scheme (ItemList)
	 * @param faqItems
	 *
	 * @see https://schema.org/FAQPage
	 */
	public getFAQSchema(faqItems: TFaqItem[] = []): string {
		return this._prepareData({
			'@type': `FAQPage`,
			mainEntity: faqItems.map(faqItem => ({
				'@type': `Question`,
				name: faqItem.question,
				acceptedAnswer: {
					'@type': `Answer`,
					text: faqItem.answer
				}
			}))
		})
	}

	/**
	 * Returns Organization info jsonLd scheme (Organization)
	 * @param breadcrumbs
	 *
	 * @see https://schema.org/Organization
	 */
	public getBreadcrumbsSchema(breadcrumbs: TPage[] = [], baseUrl: string): string {
		breadcrumbs = breadcrumbs.length ? [{ title: `Главная`, link: `` }, ...breadcrumbs] : breadcrumbs

		const currentBaseUrl = baseUrl || this._projectInfo.link

		return this._prepareData({
			'@graph': [
				{
					'@type': `BreadcrumbList`,
					itemListElement: breadcrumbs.map(({ title, link }, i) => ({
						'@type': `ListItem`,
						position: i + 1,
						item: {
							'@id': currentBaseUrl + link,
							name: !!title ? unEscape(title) : ``
						}
					}))
				}
			]
		})
	}

	private _prepareData(data: Record<string, unknown>) {
		return JSON.stringify({
			...this._commonSchemaParams,
			...data
		})
	}
}
